import React, { useState, useMemo } from 'react';
import { DEPARTMENTS, DEPT_COLORS, EVENTS } from '../data/events';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];
const MONTH_SHORT = MONTH_NAMES.map(m => m.slice(0, 3).toUpperCase());
const DAY_LABELS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const DAY_LABELS_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const NAVY = '#00384D';
const NAVY_DEEP = '#132c43';
const CRIMSON = '#C21E26';
const SAND = '#F2E8CF';

function eventDateValue(e) {
  return new Date(e.year, e.month - 1, e.day).getTime();
}

function isPastEvent(e, todayValue) {
  return eventDateValue(e) < todayValue;
}

export default function Calendar() {
  // Stable "today" so hooks below don't see a new Date() on every render.
  const today = useMemo(() => new Date(), []);
  const todayStart = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime(),
    [today]
  );

  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);

  // The 5 years shown in the archive selector: current year + previous 4.
  const availableYears = useMemo(() => {
    const y = today.getFullYear();
    return [y, y - 1, y - 2, y - 3, y - 4];
  }, [today]);

  const filtered = activeFilter === 'All' ? EVENTS : EVENTS.filter(e => e.department === activeFilter);

  function resetSelection() {
    setSelectedKey(null);
    setSelectedEvents([]);
  }

  function goToYear(year) {
    resetSelection();
    setCurrentYear(year);
    setCurrentMonth(year === today.getFullYear() ? today.getMonth() : 0);
  }

  function goToday() {
    resetSelection();
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
  }

  function prevMonth() {
    resetSelection();
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  }

  function nextMonth() {
    resetSelection();
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  }

  function handleDayClick(key, dayEvents) {
    if (selectedKey === key) resetSelection();
    else { setSelectedKey(key); setSelectedEvents(dayEvents); }
  }

  const month = currentMonth + 1;
  const firstDayOfWeek = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  const monthEvents = filtered.filter(e => e.year === currentYear && e.month === month);

  // Events belonging to the selected archive year.
  const yearEvents = useMemo(
    () => filtered.filter(e => e.year === currentYear),
    [filtered, currentYear]
  );

  const upcomingEvents = useMemo(
    () => yearEvents
      .filter(e => !isPastEvent(e, todayStart))
      .sort((a, b) => eventDateValue(a) - eventDateValue(b)),
    [yearEvents, todayStart]
  );

  const pastEvents = useMemo(
    () => yearEvents
      .filter(e => isPastEvent(e, todayStart))
      .sort((a, b) => eventDateValue(a) - eventDateValue(b)),
    [yearEvents, todayStart]
  );

  const ministriesCount = useMemo(
    () => new Set(yearEvents.map(e => e.department)).size,
    [yearEvents]
  );

  return (
    <div className="bg-white min-h-screen flex flex-col">

      {/* Hero — unchanged */}
      <section className="relative py-20 md:py-28 px-4 md:px-6 text-center overflow-hidden bg-white">
        <div className="relative z-10 max-w-4xl mx-auto">
          <span className="text-[10px] font-black text-crimson uppercase tracking-[0.4em] block mb-6">
            Western Cape District
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-6xl font-black uppercase tracking-tighter">
            <span className="text-atlantic">Events </span>
            <span className="text-atlantic italic font-serif">Calendar</span>
          </h1>
          <p className="text-base md:text-xl text-slate-500 font-light max-w-2xl mx-auto leading-relaxed mt-6">
            Stay up to date with all district events, ministries, and gatherings.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto w-full px-4 md:px-6 pb-20 flex-1 flex flex-col">

        {/* ============ YEAR NAVIGATION ============ */}
        <section className="mb-10">
          <span className="text-[10px] font-black text-crimson uppercase tracking-[0.4em] block mb-4">
            Events Archive
          </span>

          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar">
            {availableYears.map(year => {
              const isSelected = year === currentYear;
              return (
                <button
                  key={year}
                  onClick={() => goToYear(year)}
                  aria-current={isSelected ? 'true' : undefined}
                  className="shrink-0 px-5 py-3 rounded-xl text-lg md:text-xl font-black tracking-tight transition-colors border"
                  style={
                    isSelected
                      ? { backgroundColor: NAVY_DEEP, color: '#fff', borderColor: NAVY_DEEP }
                      : { backgroundColor: '#fff', color: 'rgba(0,56,77,0.45)', borderColor: SAND }
                  }
                >
                  {year}
                  {isSelected && (
                    <span
                      className="block h-[3px] w-6 mx-auto mt-1.5 rounded-full"
                      style={{ backgroundColor: CRIMSON }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* ============ YEAR SUMMARY ============ */}
        <section className="mb-10 pb-10 border-b border-sand">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter" style={{ color: NAVY_DEEP }}>
                {currentYear}
              </h2>
              <p className="text-sm text-slate-500 font-light mt-1">
                District events and ministry gatherings
              </p>
            </div>

            <div className="flex gap-6 md:gap-10">
              <div>
                <p className="text-2xl font-black" style={{ color: NAVY }}>{yearEvents.length}</p>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mt-0.5">
                  Events
                </p>
              </div>
              <div>
                <p className="text-2xl font-black" style={{ color: NAVY }}>{ministriesCount}</p>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mt-0.5">
                  Ministries
                </p>
              </div>
              <div>
                <p className="text-2xl font-black" style={{ color: CRIMSON }}>{upcomingEvents.length}</p>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mt-0.5">
                  Upcoming
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============ CALENDAR SECTION ============ */}
        <section>
          {/* Department filters */}
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 mb-6 no-scrollbar">
            {DEPARTMENTS.map(dept => {
              const isActive = activeFilter === dept;
              const c = DEPT_COLORS[dept];
              return (
                <button
                  key={dept}
                  onClick={() => { setActiveFilter(dept); resetSelection(); }}
                  className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] transition-colors border"
                  style={
                    isActive
                      ? { backgroundColor: NAVY_DEEP, color: '#fff', borderColor: NAVY_DEEP }
                      : { backgroundColor: '#fff', color: NAVY, borderColor: SAND }
                  }
                >
                  {c && (
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: isActive ? '#fff' : c.color }}
                    />
                  )}
                  {dept}
                </button>
              );
            })}
          </div>

          {/* Month navigation */}
          <div className="flex items-center justify-between mb-6 gap-3">
            <button
              onClick={prevMonth}
              aria-label="Previous month"
              className="w-11 h-11 md:w-12 md:h-12 rounded-full border border-sand flex items-center justify-center font-black text-2xl transition-colors shrink-0"
              style={{ color: NAVY }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = NAVY_DEEP; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = NAVY_DEEP; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; e.currentTarget.style.color = NAVY; e.currentTarget.style.borderColor = ''; }}
            >
              ‹
            </button>

            <div className="text-center flex-1">
              <h3 className="text-2xl md:text-4xl font-black tracking-tighter" style={{ color: NAVY_DEEP }}>
                {MONTH_NAMES[currentMonth]}
              </h3>
              <p className="text-[11px] font-black uppercase tracking-[0.3em] mt-0.5" style={{ color: 'rgba(0,56,77,0.4)' }}>
                {currentYear}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={goToday}
                className="hidden sm:inline-flex px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] border transition-colors"
                style={{ color: NAVY, borderColor: SAND }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = SAND; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                Today
              </button>
              <button
                onClick={nextMonth}
                aria-label="Next month"
                className="w-11 h-11 md:w-12 md:h-12 rounded-full border border-sand flex items-center justify-center font-black text-2xl transition-colors"
                style={{ color: NAVY }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = NAVY_DEEP; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = NAVY_DEEP; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; e.currentTarget.style.color = NAVY; e.currentTarget.style.borderColor = ''; }}
              >
                ›
              </button>
            </div>
          </div>

          {/* Mobile Today button */}
          <button
            onClick={goToday}
            className="sm:hidden mb-4 w-full py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] border"
            style={{ color: NAVY, borderColor: SAND }}
          >
            Today
          </button>

          {/* Calendar grid */}
          <div className="rounded-[1.5rem] border border-sand overflow-hidden shadow-sm">

            {/* Day headers */}
            <div className="grid grid-cols-7" style={{ backgroundColor: NAVY_DEEP }}>
              {DAY_LABELS_SHORT.map((d, i) => (
                <div key={d} className="py-3 text-center text-[10px] font-black uppercase tracking-widest" style={{ color: 'rgba(242,232,207,0.7)' }}>
                  <span className="hidden sm:inline">{DAY_LABELS[i]}</span>
                  <span className="sm:hidden">{d}</span>
                </div>
              ))}
            </div>

            {/* Weeks */}
            {weeks.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 border-t border-sand">
                {week.map((day, di) => {
                  if (!day) return (
                    <div
                      key={`e-${wi}-${di}`}
                      className="min-h-[90px] md:min-h-[130px]"
                      style={{ backgroundColor: 'rgba(242,232,207,0.08)', borderRight: di < 6 ? '1px solid #F2E8CF' : 'none' }}
                    />
                  );

                  const key = `${currentYear}-${month}-${day}`;
                  const dayEvents = filtered.filter(e => e.year === currentYear && e.month === month && e.day === day);
                  const isToday = today.getFullYear() === currentYear && today.getMonth() === currentMonth && today.getDate() === day;
                  const isSelected = selectedKey === key;
                  const hasEvents = dayEvents.length > 0;
                  const Wrapper = hasEvents ? 'button' : 'div';

                  return (
                    <Wrapper
                      key={key}
                      {...(hasEvents
                        ? {
                            onClick: () => handleDayClick(key, dayEvents),
                            'aria-pressed': isSelected,
                            'aria-label': `${MONTH_NAMES[currentMonth]} ${day}, ${currentYear} — ${dayEvents.length} event${dayEvents.length > 1 ? 's' : ''}`,
                          }
                        : { 'aria-hidden': true })}
                      className="min-h-[90px] md:min-h-[130px] p-1.5 md:p-2 flex flex-col gap-1 transition-colors text-left w-full"
                      style={{
                        borderRight: di < 6 ? '1px solid #F2E8CF' : 'none',
                        cursor: hasEvents ? 'pointer' : 'default',
                        backgroundColor: isSelected ? 'rgba(242,232,207,0.4)' : 'transparent',
                      }}
                      onMouseEnter={e => { if (hasEvents && !isSelected) e.currentTarget.style.backgroundColor = 'rgba(242,232,207,0.25)'; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {/* Day number */}
                      <span
                        className="self-start w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-full text-xs font-black"
                        style={isToday
                          ? { backgroundColor: CRIMSON, color: '#fff' }
                          : { color: '#64748b' }
                        }
                      >
                        {day}
                      </span>

                      {/* Event pills — desktop */}
                      <div className="flex flex-col gap-0.5">
                        {dayEvents.slice(0, 2).map((ev, idx) => {
                          const c = DEPT_COLORS[ev.department] || DEPT_COLORS['District'];
                          return (
                            <span
                              key={idx}
                              className="text-[9px] font-black px-1.5 py-0.5 rounded truncate hidden sm:block"
                              style={{ backgroundColor: c.color, color: '#fff' }}
                            >
                              {ev.title}
                            </span>
                          );
                        })}
                        {/* Mobile dots */}
                        {hasEvents && (
                          <div className="flex gap-0.5 sm:hidden flex-wrap mt-0.5">
                            {dayEvents.slice(0, 4).map((ev, idx) => {
                              const c = DEPT_COLORS[ev.department] || DEPT_COLORS['District'];
                              return <span key={idx} className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />;
                            })}
                          </div>
                        )}
                        {dayEvents.length > 2 && (
                          <span className="text-[9px] font-black pl-1 hidden sm:block" style={{ color: 'rgba(0,56,77,0.5)' }}>
                            +{dayEvents.length - 2} more
                          </span>
                        )}
                      </div>
                    </Wrapper>
                  );
                })}
              </div>
            ))}
          </div>

          {monthEvents.length === 0 && (
            <p className="text-center text-[10px] font-black uppercase tracking-[0.3em] mt-6" style={{ color: 'rgba(0,56,77,0.3)' }}>
              No events for {MONTH_NAMES[currentMonth]} {currentYear}
            </p>
          )}

          {/* Selected day detail panel */}
          {selectedKey && selectedEvents.length > 0 && (
            <div className="mt-8 rounded-[1.5rem] border border-sand p-6 md:p-8" style={{ backgroundColor: 'rgba(242,232,207,0.15)' }}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="h-[2px] w-10" style={{ backgroundColor: CRIMSON }} />
                  <span className="text-[10px] font-black uppercase tracking-[0.4em]" style={{ color: NAVY }}>
                    Events on {(() => {
                      const [y, m, d] = selectedKey.split('-');
                      return `${MONTH_NAMES[parseInt(m) - 1]} ${d}, ${y}`;
                    })()}
                  </span>
                </div>
                <button
                  onClick={resetSelection}
                  aria-label="Close event details"
                  className="font-black text-lg transition-colors"
                  style={{ color: 'rgba(0,56,77,0.4)' }}
                  onMouseEnter={e => e.currentTarget.style.color = CRIMSON}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(0,56,77,0.4)'}
                >
                  ✕
                </button>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {selectedEvents.map((event, i) => {
                  const c = DEPT_COLORS[event.department] || DEPT_COLORS['District'];
                  return (
                    <div
                      key={i}
                      className="bg-white rounded-[1rem] border border-sand p-5 space-y-2 shadow-sm"
                      style={{ borderLeft: `4px solid ${c.color}` }}
                    >
                      <span
                        className="inline-block px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.2em] text-white"
                        style={{ backgroundColor: c.color }}
                      >
                        {event.department}
                      </span>
                      <h4 className="font-black tracking-tight text-sm" style={{ color: NAVY_DEEP }}>{event.title}</h4>
                      <div className="text-xs text-slate-500 space-y-0.5">
                        {event.time && <p>{event.time}</p>}
                        {event.location && <p>{event.location}</p>}
                      </div>
                      {event.description && (
                        <p className="text-xs text-slate-500 leading-relaxed border-t border-sand pt-2">{event.description}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* ============ UPCOMING EVENTS ============ */}
        <section className="mt-16">
          <span className="text-[10px] font-black text-crimson uppercase tracking-[0.4em] block mb-6">
            Upcoming Events
          </span>

          {upcomingEvents.length === 0 ? (
            <p className="text-sm text-slate-400 font-light border border-sand rounded-[1rem] py-10 text-center">
              No upcoming events scheduled for this year.
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {upcomingEvents.map((event, i) => {
                const c = DEPT_COLORS[event.department] || DEPT_COLORS['District'];
                return (
                  <div
                    key={i}
                    className="flex gap-4 bg-white rounded-[1rem] border border-sand p-5 shadow-sm"
                  >
                    <div className="shrink-0 flex flex-col items-center justify-center w-14 rounded-lg" style={{ backgroundColor: SAND }}>
                      <span className="text-xl font-black" style={{ color: NAVY_DEEP }}>{event.day}</span>
                      <span className="text-[9px] font-black uppercase tracking-wider" style={{ color: NAVY }}>
                        {MONTH_SHORT[event.month - 1]}
                      </span>
                      <span className="text-[9px] text-slate-400">{event.year}</span>
                    </div>
                    <div className="min-w-0 space-y-1.5">
                      <span
                        className="inline-block px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-[0.2em] text-white"
                        style={{ backgroundColor: c.color }}
                      >
                        {event.department}
                      </span>
                      <h4 className="font-black tracking-tight text-sm" style={{ color: NAVY_DEEP }}>{event.title}</h4>
                      <p className="text-xs text-slate-500">
                        {[event.time, event.location].filter(Boolean).join(' · ')}
                      </p>
                      {event.description && (
                        <p className="text-xs text-slate-500 leading-relaxed">{event.description}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ============ EVENT ARCHIVE ============ */}
        <section className="mt-16">
          <span className="text-[10px] font-black uppercase tracking-[0.4em] block mb-1" style={{ color: 'rgba(0,56,77,0.4)' }}>
            Event Archive
          </span>
          <p className="text-sm text-slate-400 font-light mb-6">
            Browse events from {currentYear}.
          </p>

          {pastEvents.length === 0 ? (
            <p className="text-sm text-slate-400 font-light border border-sand rounded-[1rem] py-10 text-center">
              No past events recorded for this year.
            </p>
          ) : (
            <div className="divide-y divide-sand border-t border-b border-sand">
              {pastEvents.map((event, i) => {
                const c = DEPT_COLORS[event.department] || DEPT_COLORS['District'];
                return (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 py-4" style={{ borderLeft: `3px solid ${c.color}`, paddingLeft: '1rem' }}>
                    <div className="w-20 shrink-0">
                      <p className="text-sm font-black" style={{ color: 'rgba(0,56,77,0.5)' }}>
                        {MONTH_SHORT[event.month - 1]} {event.day}
                      </p>
                      <p className="text-[10px] text-slate-400">{event.year}</p>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-bold text-sm text-slate-600">{event.title}</h4>
                        <span className="text-[9px] font-black uppercase tracking-[0.15em] text-slate-400">
                          {event.department}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {[event.time, event.location].filter(Boolean).join(' · ')}
                      </p>
                      {event.description && (
                        <p className="text-xs text-slate-400 leading-relaxed mt-1">{event.description}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

      </div>
    </div>
  );
}